import { Play, Expand, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import type { MediaURL } from "../types";
import { getCachedInstagramReelMedia } from "../services/instagramCache";
import { useImageProxy } from "../hooks/useImageProxy";

interface MediaRendererProps {
    mediaInfo: MediaURL | MediaURL[] | null;
    className?: string;
    style?: React.CSSProperties;
    maxHeight?: string;
    maxWidth?: string;
    objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
    objectPosition?: "center" | "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
    showPlayButton?: boolean;
    onVideoPlay?: () => void;
    onVideoEnd?: () => void;
    onMediaView?: (mediaType: string) => void;
    fallbackImage?: string;
    post?: any; // Add post prop to access Instagram post data
}

interface ImageDimensions {
    width: number;
    height: number;
    aspectRatio: number;
}

interface ImageState {
    dimensions: ImageDimensions | null;
    loading: boolean;
    error: string | null;
    readyUrl: string | null;
}

// Module-level cache of resolved image loads, keyed by thumbnail URL. Survives component
// remounts (masonic remounts every cell when the grid's `render` identity changes — e.g.
// during the startup fetch burst or a selection toggle). Without this, each remount rebuilt
// empty imageStates and fired a fresh `new Image()` per card, hammering the Instagram CDN
// (which rate-limits → 403) and flickering the UI. With it, a remount reuses the prior
// result and never re-requests the URL.
type CachedImageResult = { dimensions: ImageDimensions | null; readyUrl: string | null; error: string | null };
const imageResultCache = new Map<string, CachedImageResult>();

export const MediaRenderer = ({
    mediaInfo,
    className = "",
    style = {},
    maxHeight = "300px",
    maxWidth,
    objectFit = "cover",
    objectPosition = "center",
    showPlayButton = true,
    onVideoPlay,
    onVideoEnd,
    onMediaView,
    fallbackImage = "/assets/placeholder.jpg",
    post
}: MediaRendererProps) => {
    const [showImagePopup, setShowImagePopup] = useState(false);
    const [showVideoPopup, setShowVideoPopup] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageStates, setImageStates] = useState<Record<number, ImageState>>({});
    const [isLoadingReel, setIsLoadingReel] = useState(false);
    const [reelVideoUrl, setReelVideoUrl] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);


    // Convert single mediaInfo to array for consistent handling
    const mediaArray = Array.isArray(mediaInfo) ? mediaInfo : mediaInfo ? [mediaInfo] : [];
    const currentMedia = mediaArray[currentIndex] || null;
    const hasMultipleMedia = mediaArray.length > 1;

    // Get proxied thumbnail URL for current media
    // Check for both id (from full Post object) and post_id (from platform-specific post)
    const postId = post?.id || post?.post_id || null;
    const proxiedThumbnailUrl = useImageProxy(
        currentMedia?.thumbnail_url || null,
        postId,
        'thumbnail',
        post?.platform
    );

    // Check if current media is an Instagram reel
    const isInstagramReel = post?.instagram_post && 
        post.instagram_post.type === 2 && // Instagram reel type
        currentMedia?.type === "image"; // Currently showing thumbnail

    // Check if this is a sample reel (has post_code like "reel_1", "reel_2", etc.)
    const isSampleReel = post?.instagram_post?.post_code && 
        /^reel_\d+$/.test(post.instagram_post.post_code);

    // Function to fetch Instagram reel video with caching
    // Uses getCachedInstagramReelMedia which stores video URLs in memory cache
    // to avoid redundant API calls for the same post codes
    const fetchInstagramReelVideo = async () => {
        if (!post?.instagram_post?.post_code) return;

        setIsLoadingReel(true);
        try {
            const data = await getCachedInstagramReelMedia(post.instagram_post.post_code);
            
            // Find the video media from the response
            const videoMedia = data.media.find(media => media.type === "video");
            
            if (videoMedia) {
                // Update the current media with video URL
                setReelVideoUrl(videoMedia.url);
                setShowVideoPopup(true);
                onMediaView?.('video');
                onVideoPlay?.();
            } else {
                // If no video found, open Instagram post directly
                const instagramUrl = `https://www.instagram.com/p/${post.instagram_post.post_code}/`;
                window.open(instagramUrl, '_blank');
            }
        } catch (error) {
            console.error('Error fetching Instagram reel video:', error);
            // Fallback: try to open Instagram post directly
            const instagramUrl = `https://www.instagram.com/p/${post.instagram_post.post_code}/`;
            window.open(instagramUrl, '_blank');
        } finally {
            setIsLoadingReel(false);
        }
    };

    const loadImage = (url: string, index: number) => {
        // Cache hit → reuse the prior result, do NOT create a new Image() / re-request the URL.
        // This is what makes remounts free: no CDN refetch, no flicker.
        const cached = imageResultCache.get(url);
        if (cached) {
            setImageStates(prev => ({
                ...prev,
                [index]: { dimensions: cached.dimensions, loading: false, error: cached.error, readyUrl: cached.readyUrl }
            }));
            return;
        }

        setImageStates(prev => ({
            ...prev,
            [index]: { ...prev[index], loading: true, error: null, readyUrl: null }
        }));

        const img = new Image();

        img.onload = () => {
            const dimensions: ImageDimensions = {
                width: img.naturalWidth,
                height: img.naturalHeight,
                aspectRatio: img.naturalWidth / img.naturalHeight
            };
            imageResultCache.set(url, { dimensions, readyUrl: url, error: null });
            setImageStates(prev => ({
                ...prev,
                [index]: { dimensions, loading: false, error: null, readyUrl: url }
            }));
        };

        img.onerror = () => {
            imageResultCache.set(url, { dimensions: null, readyUrl: null, error: 'Failed to load image' });
            setImageStates(prev => ({
                ...prev,
                [index]: {
                    ...prev[index],
                    error: 'Failed to load image',
                    loading: false,
                    readyUrl: null
                }
            }));
        };

        img.src = url;
    };

    const nextMedia = () => {
        if (hasMultipleMedia) {
            setCurrentIndex((prev) => (prev + 1) % mediaArray.length);
        }
    };

    const prevMedia = () => {
        if (hasMultipleMedia) {
            setCurrentIndex((prev) => (prev - 1 + mediaArray.length) % mediaArray.length);
        }
    };

    const goToMedia = (index: number) => {
        if (hasMultipleMedia && index >= 0 && index < mediaArray.length) {
            setCurrentIndex(index);
        }
    };

    const handlePlayVideo = () => {
        if (isInstagramReel) {
            fetchInstagramReelVideo();
        } else if (post?.platform === 'TIKTOK' && post?.tiktok_post?.post_url) {
            // TikTok videos are blocked by ORB, so open the post URL directly
            window.open(post.tiktok_post.post_url, '_blank', 'noopener,noreferrer');
            onMediaView?.('video');
            onVideoPlay?.();
        } else if (post?.platform === 'REDDIT' && post?.reddit_post?.post_url) {
            window.open(post.reddit_post.post_url, '_blank', 'noopener,noreferrer');
            onMediaView?.('video');
            onVideoPlay?.();
        } else if (currentMedia?.video_url) {
            setShowVideoPopup(true);
            onMediaView?.('video');
            onVideoPlay?.();
        }
    };

    const handleCloseVideoPopup = () => {
        setShowVideoPopup(false);
        onVideoEnd?.();
    };

    const handleExpandImage = () => {
        setShowImagePopup(true);
        onMediaView?.('image');
    };

    const handleClosePopup = () => {
        setShowImagePopup(false);
    };

    // Load all images when mediaArray changes
    // Note: We use the original URLs for loading state, but display proxied URLs
    useEffect(() => {
        mediaArray.forEach((media, index) => {
            if (media?.thumbnail_url && !imageStates[index]) {
                // Use original URL for loading dimensions, proxied URL will be used for display
                loadImage(media.thumbnail_url, index);
            }
        });
    }, [mediaArray.map(m => m?.thumbnail_url).join(',')]);

    // Reset currentIndex if it's out of bounds for the current mediaArray
    useEffect(() => {
        if (currentIndex >= mediaArray.length && mediaArray.length > 0) {
            setCurrentIndex(0);
        }
    }, [currentIndex, mediaArray.length]);

    if (!mediaInfo || mediaArray.length === 0) {
        return (
            <div
                className={`bg-gray-700 rounded-lg flex items-center justify-center ${className}`}
                style={{ height: maxHeight || '192px', ...style }}
            >
                <div className="text-gray-400">
                    <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                </div>
            </div>
        );
    }

    const isVideo = (currentMedia?.type === "video" && !!currentMedia?.video_url) || 
                   (post?.platform === 'LINKEDIN' && currentMedia?.type === 'video') ||
                   (post?.platform === 'TIKTOK' && currentMedia?.type === 'video') ||
                   (post?.platform === 'REDDIT' && currentMedia?.type === 'video');
    const isImage = currentMedia?.type === "image";
    const shouldShowPlayButton = (isVideo && showPlayButton) || (isInstagramReel && showPlayButton && !isSampleReel);
    const currentImageState = imageStates[currentIndex] || { dimensions: null, loading: false, error: null, readyUrl: null };

    // Show image with optional play button overlay and expand icon
    return (
        <>
            <div className={`relative group overflow-hidden ${className}`} style={{ ...style, maxHeight }}>
                {currentImageState.loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800/50 rounded-lg z-10" style={{ minHeight: maxHeight }}>
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-500" />
                    </div>
                )}

                {currentImageState.error ? (
                    <div className="absolute inset-0 bg-gray-800 rounded-lg flex items-center justify-center text-gray-400" style={{ minHeight: maxHeight }}>
                        <span>Failed to load image</span>
                    </div>
                ) : (
                    <div className="relative w-full overflow-hidden rounded-lg" style={{ maxHeight, height: maxHeight }}>
                        <img
                            src={currentImageState.readyUrl ?? proxiedThumbnailUrl ?? fallbackImage}
                            alt="Media"
                            className="w-full rounded-lg"
                            style={{
                                width: '100%',
                                height: '100%',
                                maxHeight,
                                maxWidth: maxWidth || '100%',
                                objectFit,
                                objectPosition,
                                display: 'block'
                            }}
                            onError={() => loadImage(fallbackImage, currentIndex)}
                        />

                        {/* Dark overlay on image hover for images */}
                        {isImage && (
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" />
                        )}

                        {/* Carousel navigation arrows - only show if multiple media */}
                        {hasMultipleMedia && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        prevMedia();
                                    }}
                                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-70 hover:bg-opacity-90 transition-all duration-200 rounded-full p-2 opacity-0 group-hover:opacity-100 z-30"
                                    style={{ pointerEvents: 'auto' }}
                                >
                                    <ChevronLeft className="w-5 h-5 text-white" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        nextMedia();
                                    }}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-70 hover:bg-opacity-90 transition-all duration-200 rounded-full p-2 opacity-0 group-hover:opacity-100 z-30"
                                    style={{ pointerEvents: 'auto' }}
                                >
                                    <ChevronRight className="w-5 h-5 text-white" />
                                </button>
                            </>
                        )}

                        {shouldShowPlayButton && (
                            <button
                                onClick={handlePlayVideo}
                                disabled={isLoadingReel}
                                className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 hover:bg-opacity-60 transition-all duration-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="bg-white bg-opacity-90 rounded-full p-3">
                                    {isLoadingReel ? (
                                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-500" />
                                    ) : (
                                        <Play className="w-6 h-6 text-black fill-current" />
                                    )}
                                </div>
                            </button>
                        )}

                        {/* Expand icon for images */}
                        {isImage && !shouldShowPlayButton && (
                            <button
                                onClick={handleExpandImage}
                                className="absolute bottom-2 right-2 bg-black bg-opacity-50 hover:bg-opacity-70 transition-all duration-200 rounded-full p-2 opacity-0 group-hover:opacity-100 z-30"
                            >
                                <Expand className="w-5 h-5 text-white" />
                            </button>
                        )}
                    </div>
                )}

                {/* Carousel indicators/dots - only show if multiple media */}
                {hasMultipleMedia && (
                    <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-2 z-30">
                        {mediaArray.map((_, index) => (
                            <div
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    goToMedia(index);
                                }}
                                className={`w-2 h-2 rounded-full transition-all duration-200 ${index === currentIndex
                                        ? 'bg-white shadow-lg'
                                        : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                                    }`}
                                style={{ pointerEvents: 'auto' }}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Image Popup Modal */}
            {showImagePopup && isImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50 p-4"
                    onClick={handleClosePopup}
                >
                    <div className="relative w-full h-full flex items-center justify-center">
                        <button
                            onClick={handleClosePopup}
                            className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200 rounded-full p-2 text-white z-10"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Carousel navigation in popup - only show if multiple media */}
                        {hasMultipleMedia && (
                            <>
                                <button
                                    onClick={(e) => { e.stopPropagation(); prevMedia(); }}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200 rounded-full p-3 text-white z-10"
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); nextMedia(); }}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200 rounded-full p-3 text-white z-10"
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                            </>
                        )}

                        <img
                            src={currentImageState.readyUrl ?? proxiedThumbnailUrl ?? fallbackImage}
                            alt="Full size media"
                            className="max-w-full max-h-full object-contain rounded-lg"
                            style={{
                                maxWidth: 'calc(100vw - 2rem)',
                                maxHeight: 'calc(100vh - 2rem)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        />

                        {/* Media counter in popup */}
                        {hasMultipleMedia && (
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm">
                                {currentIndex + 1} / {mediaArray.length}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Video Popup Modal */}
            {showVideoPopup && (isVideo || isInstagramReel) && !(post?.platform === 'TIKTOK') && !(post?.platform === 'REDDIT') && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50 p-4"
                    onClick={handleCloseVideoPopup}
                >
                    <div className="relative w-full h-full flex items-center justify-center">
                        <button
                            onClick={handleCloseVideoPopup}
                            className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200 rounded-full p-2 text-white z-10"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Carousel navigation in video popup - only show if multiple media */}
                        {hasMultipleMedia && (
                            <>
                                <button
                                    onClick={(e) => { e.stopPropagation(); prevMedia(); }}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200 rounded-full p-3 text-white z-10"
                                >
                                    <ChevronLeft className="w-6 h-6" />
                                </button>
                                <button
                                    onClick={(e) => { e.stopPropagation(); nextMedia(); }}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 transition-all duration-200 rounded-full p-3 text-white z-10"
                                >
                                    <ChevronRight className="w-6 h-6" />
                                </button>
                            </>
                        )}

                        {/* Video element */}
                        <video
                            ref={videoRef}
                            src={reelVideoUrl || currentMedia?.video_url}
                            controls
                            autoPlay
                            className="max-w-full max-h-full object-contain rounded-lg"
                            style={{
                                maxWidth: 'calc(100vw - 2rem)',
                                maxHeight: 'calc(100vh - 2rem)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onEnded={handleCloseVideoPopup}
                            onError={(e) => {
                                // If video fails to load (e.g., ORB error), show error message
                                console.error('Video load error:', e);
                                const videoElement = e.currentTarget;
                                videoElement.style.display = 'none';
                                const errorDiv = document.createElement('div');
                                errorDiv.className = 'flex flex-col items-center justify-center p-8 text-white';
                                errorDiv.innerHTML = `
                                    <p class="text-lg font-semibold mb-2">Unable to load video</p>
                                    <p class="text-sm text-gray-300 mb-4">The video could not be loaded due to browser security restrictions.</p>
                                `;
                                videoElement.parentElement?.appendChild(errorDiv);
                            }}
                        />

                        {/* Media counter in video popup */}
                        {hasMultipleMedia && (
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm">
                                {currentIndex + 1} / {mediaArray.length}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

// Helper function to get media info from a post (can be used by other components)
export const getMediaInfo = (post: any): MediaURL | MediaURL[] | null => {
    
    if (!post) {
        return null;
    }

   // Build a flexible list of potential media arrays from different shapes (Instagram, X, LinkedIn, TikTok, generic)
   const mediaCandidates: any[] = [];
   if (Array.isArray(post.media_urls)) mediaCandidates.push(post.media_urls);
   if (Array.isArray(post.media)) mediaCandidates.push(post.media);
   if (post.instagram_post) {
       if (Array.isArray(post.instagram_post.media_urls)) mediaCandidates.push(post.instagram_post.media_urls);
       if (Array.isArray(post.instagram_post.media)) mediaCandidates.push(post.instagram_post.media);
   }
   if (post.x_post) {
       if (Array.isArray(post.x_post.media_urls)) mediaCandidates.push(post.x_post.media_urls);
       if (Array.isArray(post.x_post.media)) mediaCandidates.push(post.x_post.media);
       if (post.x_post.entities?.media && Array.isArray(post.x_post.entities.media)) mediaCandidates.push(post.x_post.entities.media);
       if (post.x_post.extended_entities?.media && Array.isArray(post.x_post.extended_entities.media)) mediaCandidates.push(post.x_post.extended_entities.media);
   }
   if (post.linkedin_post) {
       if (Array.isArray(post.linkedin_post.media_urls)) mediaCandidates.push(post.linkedin_post.media_urls);
       if (Array.isArray(post.linkedin_post.media)) mediaCandidates.push(post.linkedin_post.media);
       if (Array.isArray(post.linkedin_post.images)) mediaCandidates.push(post.linkedin_post.images);
       if (Array.isArray(post.linkedin_post.videos)) mediaCandidates.push(post.linkedin_post.videos);
       if (Array.isArray(post.linkedin_post.documents)) mediaCandidates.push(post.linkedin_post.documents);
   }
   if (post.tiktok_post) {
       if (Array.isArray(post.tiktok_post.media_urls)) mediaCandidates.push(post.tiktok_post.media_urls);
   }
   if (post.reddit_post) {
       if (Array.isArray(post.reddit_post.media_urls)) mediaCandidates.push(post.reddit_post.media_urls);
   }

   const primaryMediaArray = mediaCandidates.find(arr => Array.isArray(arr) && arr.length > 0);

    // Handle different post structures
    if (primaryMediaArray && primaryMediaArray.length > 0) {
        const mediaItems = primaryMediaArray.map((mediaItem: any) => {
            // Check if mediaItem is an object with the new structure
            if (typeof mediaItem === 'object' && mediaItem !== null && 'thumbnail_url' in mediaItem) {
                return {
                    type: (mediaItem as MediaURL).type || "image",
                    thumbnail_url: (mediaItem as MediaURL).thumbnail_url,
                    video_url: (mediaItem as MediaURL).video_url
                };
            }
            // Check if mediaItem is an object with LinkedIn structure (thumbnailUrl property)
            else if (typeof mediaItem === 'object' && mediaItem !== null && 'thumbnailUrl' in mediaItem) {
                const linkedinMedia = mediaItem as any;
                return {
                    type: linkedinMedia.type === 'video' ? "video" : "image",
                    thumbnail_url: linkedinMedia.thumbnailUrl,
                    video_url: linkedinMedia.type === 'video' ? linkedinMedia.url : undefined
                };
            }
            // Check if mediaItem is an object with LinkedIn image structure (url property)
            else if (typeof mediaItem === 'object' && mediaItem !== null && 'url' in mediaItem && 'type' in mediaItem) {
                const linkedinImage = mediaItem as any;
                return {
                    type: linkedinImage.type === 'video' ? "video" : "image",
                    thumbnail_url: linkedinImage.url,
                    video_url: linkedinImage.type === 'video' ? linkedinImage.url : undefined
                };
            }
            // Handle X (Twitter) media entities (media_url[_https], video_info)
            else if (typeof mediaItem === 'object' && mediaItem !== null && ('media_url_https' in mediaItem || 'media_url' in mediaItem || 'video_info' in mediaItem)) {
                const xMedia = mediaItem as any;
                const isVideo = xMedia.type === 'video' || !!xMedia.video_info;
                const videoUrl = xMedia.video_info?.variants?.find((v: any) => v?.content_type?.includes('mp4'))?.url || xMedia.video_info?.variants?.[0]?.url;
                const imageUrl = xMedia.media_url_https || xMedia.media_url || xMedia.url;
                return {
                    type: isVideo ? "video" : "image",
                    thumbnail_url: imageUrl,
                    video_url: isVideo ? videoUrl : undefined
                };
            }
            // Fallback for string URLs
            else if (typeof mediaItem === 'string') {
                const mediaUrl = mediaItem as string;

                // Check if it's a video URL (common video extensions)
                const isVideo = mediaUrl.includes('.mp4') ||
                    mediaUrl.includes('.mov') ||
                    mediaUrl.includes('.avi') ||
                    mediaUrl.includes('.webm') ||
                    mediaUrl.includes('video');

                if (isVideo) {
                    return {
                        type: "video" as const,
                        thumbnail_url: mediaUrl, // Use the same URL as thumbnail for now
                        video_url: mediaUrl
                    };
                } else {
                    return {
                        type: "image" as const,
                        thumbnail_url: mediaUrl
                    };
                }
            }
            return null;
        }).filter(Boolean) as MediaURL[];

        // Return array if multiple items, single item if only one
        return mediaItems.length === 1 ? mediaItems[0] : mediaItems;
    }

    // Handle Instagram posts with single thumbnail fallback
    if (post.thumbnail_url) {
        return {
            type: "image",
            thumbnail_url: post.thumbnail_url
        };
    }
    
    // Handle Instagram nested post with single thumbnail
    if (post.instagram_post?.thumbnail_url) {
        return {
            type: post.instagram_post.type === 2 ? "video" : "image", // type 2 is video
            thumbnail_url: post.instagram_post.thumbnail_url
        };
    }
    
    // Handle LinkedIn nested post with single thumbnail
    if (post.linkedin_post?.thumbnail_url) {
        return {
            type: "image",
            thumbnail_url: post.linkedin_post.thumbnail_url
        };
    }

    // Handle TikTok posts with single thumbnail fallback
    if (post.tiktok_post?.thumbnail_url) {
        // For TikTok, check if there are media_urls to determine if it's a video
        const hasVideo = post.tiktok_post.media_urls && post.tiktok_post.media_urls.length > 0;
        return {
            type: hasVideo ? "video" : "image",
            thumbnail_url: post.tiktok_post.thumbnail_url,
            video_url: hasVideo && post.tiktok_post.media_urls[0]?.video_url ? post.tiktok_post.media_urls[0].video_url : undefined
        };
    }

    if (post.reddit_post?.thumbnail_url) {
        const hasVideo = post.reddit_post.media_urls && post.reddit_post.media_urls.length > 0;
        return {
            type: hasVideo ? "video" : "image",
            thumbnail_url: post.reddit_post.thumbnail_url,
            video_url: hasVideo && post.reddit_post.media_urls[0]?.video_url ? post.reddit_post.media_urls[0].video_url : undefined
        };
    }

    return null;
}; 